// src/App.js
import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ApiProvider } from './api/apiContext'; // Import the API provider
import Header from './components/Header/Header';
import Login from './pages/Login/Login';
import Signup from './pages/Login/Signup';
import ResetPassword from './pages/Login/ResetPassword';
import ResetPasswordConfirm from './pages/Login/ResetPasswordConfirm';
import AdminLayout from './components/Admin/AdminLayout';
import PublicProfile from './pages/PublicProfile/PublicProfile';
import PublicProfileWrapper from './pages/PublicProfile/PublicProfileWrapper';
import QuestionReceived from './components/Admin/QuestionReceived';
import AccountSettings from './components/Admin/AccountSettings';
import Payment from './components/Admin/Payment';
import QuestionAsked from './components/Admin/QuestionAsked';
import QuestionCurated from './components/Admin/QuestionCurated';
import MenuPanel from './components/UI/MenuPanel';
import SupportModal from './components/Modals/SupportModal';
import QuestionPage from './pages/Question/QuestionPage';

// Import legal pages
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';

function AppContent() {
  const { currentUser, authLoading } = useContext(AuthContext);

  // ✅ Hooks must always run in the same order
  const [menuOpen, setMenuOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (currentUser) {
      document.documentElement.style.setProperty('--theme-primary', currentUser.themePrimary || '#1A140F');
      document.documentElement.style.setProperty('--theme-accent', currentUser.themeAccent || '#FAF6F2');
      document.documentElement.style.setProperty('--theme-primary-text', currentUser.themePrimaryText || '#FAF6F2');
      document.documentElement.style.setProperty('--theme-accent-text', currentUser.themeAccentText || '#1A140F');
      document.documentElement.style.setProperty('--theme-background', currentUser.themeBackground || '#FFFFFF');
      document.documentElement.style.setProperty('--theme-background-text', currentUser.themeBackgroundText || '#1A140F');
    } else {
      document.documentElement.style.setProperty('--theme-primary', '#1A140F');
      document.documentElement.style.setProperty('--theme-accent', '#FAF6F2');
      document.documentElement.style.setProperty('--theme-primary-text', '#FAF6F2');
      document.documentElement.style.setProperty('--theme-accent-text', '#1A140F');
      document.documentElement.style.setProperty('--theme-background', '#FFFFFF');
      document.documentElement.style.setProperty('--theme-background-text', '#1A140F');
    }
  }, [currentUser, authLoading]);

  // ✅ Now you can return early (after hooks)
  if (authLoading) return null;

  const toggleMenu = () => setMenuOpen(prev => !prev);
  const handleMenuClose = () => setMenuOpen(false);
  const openSupportModal = () => setSupportModalOpen(true);
  const closeSupportModal = () => setSupportModalOpen(false);

  return (
    <>
      <div className={`main-content ${menuOpen ? 'menu-open' : ''}`}>
        <Header toggleMenu={toggleMenu} menuOpen={menuOpen} />
        <Routes>
          <Route path="/" element={<PublicProfileWrapper />} />
          <Route
            path="/:userId/ask/:profileSlug/question/:questionId/:slug?"
            element={<QuestionPage />}
          />
          <Route path="/:userId/ask/:profileSlug" element={<PublicProfile />} />
          <Route path="/ask/:username/question/:questionId/:slug?" element={<QuestionPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
          <Route path="/ask/:username" element={<PublicProfile />} />
          <Route path="/terms-of-use" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/admin/*"
            element={currentUser ? <AdminLayout /> : <Navigate to="/login" replace />}
          >
            <Route index element={<QuestionReceived />} />
            <Route path="account-settings" element={<AccountSettings />} />
            <Route path="payment" element={<Payment />} />
            <Route path="questions-asked" element={<QuestionAsked />} />
            <Route path="questions-curated" element={<QuestionCurated />} />
          </Route>
        </Routes>
      </div>

      <CSSTransition in={menuOpen} timeout={400} classNames="slide" unmountOnExit>
        <MenuPanel onMenuClose={handleMenuClose} onSupportClick={openSupportModal} />
      </CSSTransition>

      {supportModalOpen && <SupportModal onClose={closeSupportModal} />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ApiProvider>
        <Router>
          <AppContent />
        </Router>
      </ApiProvider>
    </AuthProvider>
  );
}

export default App;
